// The "what changed" cell of every history surface: field, then the initial
// value and the new one, read as a diff (wireframe components/Modificari.tsx).
//
// This is the one place five surfaces — geography, medicine, medicine-code,
// site and Registrar history — turn a stored value into something a person
// reads, so a value family is taught here once rather than on each screen
// (docs/ui-shape.md: no screen prints a raw uuid, an ltree path or an MPass
// organisation code; the same rule covers a raw enum and a raw ISO instant).
import { useTranslate } from '@tolgee/react';
import { REGISTRAR_KINDS } from '@simsm/shared-types';
import { EMPTY_CELL, formatDateTime } from '@simsm/ui-shared';
import { HistoryMeta } from './HistoryMeta';
import {
  HISTORY_REASONS,
  REFERENCE_MISSING,
  type HistoryEntry,
  type HistoryFieldChange,
} from './api';

const BOOLEAN_FIELDS = new Set(['active']);

// A storage site's two block levels (CF-48). Each says whether that level's
// block is set, not whether the site is active, so it reads "yes / no" under
// the label the status tag uses (docs/shape-uc-30-sites.md §11).
const BLOCK_FIELDS = new Set(['blockedBySystemAdmin', 'blockedByRegistrar']);

// A creation lists every field it set. A block level created clear is not
// something anyone did, and every site created active would carry two lines
// saying so.
function isReported(change: HistoryFieldChange): boolean {
  return !(BLOCK_FIELDS.has(change.field) && !change.before && change.after === 'false');
}

// The fields that hold a geography id. Their value is a name the server resolved,
// or REFERENCE_MISSING when the position is gone from the nomenclator and from
// its own journal — never the raw uuid, which no screen may print (ui-shape.md).
const REFERENCE_FIELDS = new Set(['parentId', 'geographyId']);

// `kind` is the CUATM classifier, stored as the machine enum the tree one route
// over renders as "Comună / sat". The log printed COMMUNE / LOCALITY / RAION raw
// under a correctly translated label; `geography.kind.*` already exists in all
// three languages, so the value only had to be routed through it. Unknown values
// pass through: a label the interface cannot translate is better read raw than
// as its own key.
const GEOGRAPHY_KINDS = new Set([
  'COUNTRY',
  'REGION',
  'RAION',
  'MUNICIPALITY',
  'SECTOR',
  'CITY',
  'COMMUNE',
  'LOCALITY',
]);

// The same problem one entity over: `kind` on a Registrar is the operational
// typology, stored as the machine enum the register renders as "Depozit
// farmaceutic". The log printed WAREHOUSE / PHARMACY raw under a correctly
// translated label. The two sets are disjoint, so one `kind` field can serve
// both without the renderer needing to know which entity a row is about.
const REGISTRAR_KIND_SET: ReadonlySet<string> = new Set(REGISTRAR_KINDS);

const THRESHOLD_KINDS = new Set(['QUANTITY', 'PERCENT']);

// What RSUD last reported about a Registrar (UC21-04): the lifecycle token MConnect returns,
// which the register renders through `registrars.rsud.statut.*`.
const LEGAL_STATUSES = new Set(['ACTIV', 'INACTIV', 'RADIAT', 'SUSPENDAT']);

// Where a medicine code came from. The nomenclator log printed
// NOMENCLATOR_AUTORIZAT / NOMENCLATOR_NEAUTORIZAT raw under a correctly
// translated label, while the register one route over renders the same value
// through `medicines.source.*` — which ships in all three languages.
const MEDICINE_SOURCES = new Set([
  'NOMENCLATOR_AUTORIZAT',
  'NOMENCLATOR_NEAUTORIZAT',
  'MANUAL_IMPORT',
  'MANUAL_EDIT',
]);

// Whether the code is an authorised position. `medicines.kind.*` is the same
// vocabulary the medicine register and its filters use.
const AUTHORIZATION_STATUSES = new Set(['AUTHORIZED', 'UNAUTHORIZED']);

// A storage site's type, stored lower-case (`catalog_site_tip`). The site
// register renders it through `sites.tip.*`; its history printed the stored
// token, which stays Romanian-looking in every language and so reads as
// untranslated text on the RU and EN interfaces rather than as a type.
const SITE_TIPS = new Set(['farmacie', 'depozit', 'imsp', 'cabinet', 'altele']);

// The snapshots name an instant-valued field with an `Iso` suffix
// (`staleSinceIso`, and any future one), and the value is the ISO-8601 string
// the service stored. Left alone it reached the screen as
// `2026-08-30T11:04:02.517Z` beside dates the same page had already formatted —
// the UC20-09/UC20-10 staleness sweep is the writer that produces them. Nothing
// in dev carries a non-null one yet, which is why 1539 green unit tests and the
// audit's own screen sweep both missed it: the path exists, no row travels it.
function isInstantField(field: string): boolean {
  return field.endsWith('Iso');
}

// One value of the log, formatted for reading. Shared with the entity-identity
// line, which shows the same `kind` under the same rules.
//
// Every family below is matched on the field AND on the value being one this
// interface has copy for. An unknown value passes through unchanged: a label the
// interface cannot translate is better read raw than as its own translation key,
// and the role codes are open (caiet UC61-03), so a closed list here would be a
// claim the product cannot keep.
export function formatIdentityValue(
  field: string,
  value: string,
  t: (key: string) => string,
): string {
  if (field === 'kind' && GEOGRAPHY_KINDS.has(value)) {
    return t(`geography.kind.${value}`);
  }
  if (field === 'kind' && REGISTRAR_KIND_SET.has(value)) {
    return t(`registrars.kind.${value}`);
  }
  // A watched group's threshold is a quantity or a percentage (specs/060); the stored token is the
  // machine enum, which the group register words as "Cantitate" / "Procent".
  if (field === 'thresholdKind' && THRESHOLD_KINDS.has(value)) {
    return t(`watched_groups.threshold.kind.${value}`);
  }
  if (field === 'legalStatus' && LEGAL_STATUSES.has(value)) {
    return t(`registrars.rsud.statut.${value}`);
  }
  if (field === 'source' && MEDICINE_SOURCES.has(value)) {
    return t(`medicines.source.${value}`);
  }
  if (field === 'authorizationStatus' && AUTHORIZATION_STATUSES.has(value)) {
    return t(`medicines.kind.${value}`);
  }
  if (field === 'tip' && SITE_TIPS.has(value)) {
    return t(`sites.tip.${value}`);
  }
  if (isInstantField(field)) {
    // `formatDateTime` returns its input unchanged when it is not a date, so a
    // field that ends in `Iso` and does not hold one is still shown as stored.
    return formatDateTime(value);
  }
  return value;
}

export function formatHistoryValue(
  change: HistoryFieldChange,
  side: 'before' | 'after',
  t: (key: string) => string,
): string {
  const value = change[side];
  // An em dash, as the wireframe writes an absent value (`Modificari.tsx`:
  // `{s.vechi || '—'}`), and as the nomenclator table writes an empty cell. The
  // middle dot this used to print reads as a bullet that lost its list, and a
  // creation event is a whole column of them.
  if (!value) return EMPTY_CELL;
  if (change.redacted) return t('history.value.set');
  if (BOOLEAN_FIELDS.has(change.field)) {
    return value === 'true' ? t('history.value.active') : t('history.value.inactive');
  }
  if (BLOCK_FIELDS.has(change.field)) {
    return value === 'true' ? t('history.value.yes') : t('history.value.no');
  }
  if (REFERENCE_FIELDS.has(change.field) && value === REFERENCE_MISSING) {
    return t('history.value.reference_missing');
  }
  return formatIdentityValue(change.field, value, t);
}

// Everything this cell actually reads. Stated as its own type so a trail that
// is not one of the four catalog kinds can use the same renderer — the
// Registrar's own history (caiet UC21-10) lives in its own table and is not a
// `HistoryEntityKind`, and a second copy of this cell is how two screens start
// telling the same event two different ways.
export type HistoryChangeSet = Pick<HistoryEntry, 'changeType' | 'changes' | 'reason'>;

export function HistoryChanges({ entry }: { entry: HistoryChangeSet }): JSX.Element {
  const { t } = useTranslate();

  // WHY, under WHAT changed. Three events read identically without it — a code
  // inactivated by hand, one that went down with its position, and one the
  // UC20-09/UC20-10 sweep retired for age are all `Statut: activ → inactiv`.
  // A row that carries no reason (every row written before ruling 8, and every
  // write whose diff already says it all) renders none rather than a guess.
  const known = HISTORY_REASONS.find((candidate) => candidate === entry.reason);
  const reason = known ? (
    <HistoryMeta label={t('history.meta.label.reason')} value={t(`history.reason.${known}`)} />
  ) : null;

  // Every field of the event can be one the log deliberately withholds (a
  // rowVersion-only write, a nomenclator sync heartbeat). Saying "updated"
  // there would be a half-truth: the write touched the row and changed nothing
  // a reader can see, and after a sync that is most of the page.
  const changes = entry.changes.filter(isReported);
  if (changes.length === 0) {
    return (
      <>
        <span className="simsm-history__changes-empty">
          {entry.changeType === 'UPDATED'
            ? t('history.change.none')
            : t(`history.change_type.${entry.changeType}`)}
        </span>
        {reason}
      </>
    );
  }

  return (
    <>
      <dl className="simsm-history__changes">
        {changes.map((change) => (
          <div key={change.field} className="simsm-history__change">
            <dt className="simsm-history__change-field">{t(`history.field.${change.field}`)}</dt>
            <dd className="simsm-history__change-values">
              <span
                className={`simsm-history__change-before${
                  change.before ? '' : ' simsm-history__change-before--absent'
                }`}
              >
                {formatHistoryValue(change, 'before', t)}
              </span>
              <span className="simsm-history__change-arrow" aria-hidden="true">
                →
              </span>
              <span className="simsm-history__change-after">
                {formatHistoryValue(change, 'after', t)}
              </span>
            </dd>
          </div>
        ))}
      </dl>
      {reason}
    </>
  );
}
